import { useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  acceptAllMutation,
  createImportMutation,
  deleteImportMutation,
  dismissAllMutation,
  getImportOptions,
  type KgImportOut,
  listImportsOptions,
  listImportsQueryKey,
  listInboxQueryKey,
  listWikiFilesQueryKey,
  uploadImportMutation,
} from "@/api";

export type { KgImportOut as KnowledgeImport } from "@/api";

const POLL_MS = 2000;

export function isRunning(row: Pick<KgImportOut, "status">): boolean {
  return row.status === "queued" || row.status === "extracting";
}

/** Your recent imports (all spaces, or one). Polls while any is still running. */
export function useImports(spaceUuid?: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    ...listImportsOptions({ query: spaceUuid ? { space_uuid: spaceUuid } : {} }),
    enabled,
    select: (r) => r.data.imports,
    refetchInterval: (query) => (query.state.data?.data.imports.some(isRunning) ? POLL_MS : false),
  });
}

/** One import, polled until it finishes. When it does, the inbox has new proposals: refresh it. */
export function useImport(importUuid: string | null) {
  const queryClient = useQueryClient();
  const query = useQuery({
    ...getImportOptions({ path: { import_uuid: importUuid ?? "" } }),
    enabled: Boolean(importUuid),
    select: (r) => r.data,
    refetchInterval: (q) => (q.state.data && !isRunning(q.state.data.data) ? false : POLL_MS),
  });
  const finished = query.data ? !isRunning(query.data) : false;
  useEffect(() => {
    if (!finished) return;
    void queryClient.invalidateQueries({ queryKey: listInboxQueryKey() });
    void queryClient.invalidateQueries({ queryKey: listImportsQueryKey() });
  }, [finished, queryClient]);
  return query;
}

/** Start an import: upload a file, or paste text / pick a space file. */
export function useStartImport() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: listImportsQueryKey() });
  return {
    upload: useMutation({ ...uploadImportMutation(), onSuccess: refresh }),
    create: useMutation({ ...createImportMutation(), onSuccess: refresh }),
  };
}

function useRefreshAfterBulk() {
  const queryClient = useQueryClient();
  return (spaceUuid?: string | null) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: listInboxQueryKey() }),
      queryClient.invalidateQueries({ queryKey: listImportsQueryKey() }),
      spaceUuid
        ? queryClient.invalidateQueries({ queryKey: listWikiFilesQueryKey({ path: { space_uuid: spaceUuid } }) })
        : undefined,
    ]);
}

/** Accept all of an import's pending facts as proposed. */
export function useAcceptAll() {
  const refresh = useRefreshAfterBulk();
  return useMutation({ ...acceptAllMutation(), onSuccess: (res) => refresh(res.data.spaceUuid) });
}

/** Dismiss all of an import's pending facts. */
export function useDismissAll() {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const refresh = useRefreshAfterBulk();
  const message = useImportErrorMessage();
  return useMutation({
    ...dismissAllMutation(),
    onSuccess: (res) => {
      toast.success(t("import.dismissedAll", { count: res.data.dismissed }));
      return refresh();
    },
    onError: (error) => toast.error(message(error, t("import.bulkFailed"))),
  });
}

/** Cancel a running import, or remove a finished one with its pending facts. */
export function useDeleteImport() {
  const refresh = useRefreshAfterBulk();
  return useMutation({ ...deleteImportMutation(), onSuccess: () => refresh() });
}

// Request errors (`ApiError.code`) and a failed import's `errorCode`, translated.
const IMPORT_ERRORS = new Set([
  "KG_IMPORT_UNSUPPORTED",
  "KG_IMPORT_TOO_LARGE",
  "KG_IMPORT_BUSY",
  "KG_IMPORT_RUNNING",
  "KG_IMPORT_DISABLED",
  "KG_IMPORT_NOT_FOUND",
  "KG_IMPORT_UNREADABLE",
  "KG_IMPORT_EMPTY",
  "KG_IMPORT_FAILED",
  "KG_IMPORT_INTERRUPTED",
  "WIKI_PUBLISH_FORBIDDEN",
]);
type ImportErrorCode =
  | "KG_IMPORT_UNSUPPORTED"
  | "KG_IMPORT_TOO_LARGE"
  | "KG_IMPORT_BUSY"
  | "KG_IMPORT_RUNNING"
  | "KG_IMPORT_DISABLED"
  | "KG_IMPORT_NOT_FOUND"
  | "KG_IMPORT_UNREADABLE"
  | "KG_IMPORT_EMPTY"
  | "KG_IMPORT_FAILED"
  | "KG_IMPORT_INTERRUPTED"
  | "WIKI_PUBLISH_FORBIDDEN";

/** Translate an import error: an `ApiError` from a request, or a failed import's `errorCode`. */
export function useImportErrorMessage() {
  const { t } = useTranslation("knowledge");
  return (error: unknown, fallback: string): string => {
    let code: string | null | undefined;
    if (error instanceof ApiError) code = error.code;
    else if (typeof error === "string") code = error;
    if (code && IMPORT_ERRORS.has(code)) return t(`import.errors.${code as ImportErrorCode}`);
    return error instanceof ApiError ? (error.detail ?? fallback) : fallback;
  };
}
